import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, MapPin, Phone, Package, Ruler, ClipboardList, ImageIcon, Upload, FileText, X, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { toast } from "sonner";
import { getQuote, getMediaUrl, updateItem, updateQuote, uploadMedia, deleteMedia, CLIENT_COLORS } from "@/lib/api";
import api from "@/lib/api";
import { formatCurrency } from "@/lib/calculations";
import { useIsMobile } from "@/components/custom/AppShell";

const HOURS_PER_DAY = 8;
function formatDuration(h) {
  if (!h || h === 0) return "—";
  if (h >= HOURS_PER_DAY) {
    const d = h / HOURS_PER_DAY;
    return `${d % 1 === 0 ? d : d.toFixed(1)} day${d !== 1 ? "s" : ""} (${h}h)`;
  }
  return `${h} hrs`;
}

export default function JobSheet() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [enlargedPhoto, setEnlargedPhoto] = useState(null);
  const [uploading, setUploading] = useState(false);

  const loadQuote = useCallback(async () => {
    try {
      const data = await getQuote(id);
      setQuote(data);
    } catch {
      toast.error("Job not found");
      navigate("/");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { setLoading(true); loadQuote(); }, [loadQuote]);

  if (loading || !quote) {
    return <div className="min-h-screen bg-slate-50 flex items-center justify-center"><p className="text-slate-400">Loading...</p></div>;
  }

  const photos = (quote.media || []).filter(m => m.category === "photo");
  const receipts = (quote.media || []).filter(m => m.category === "receipt");
  const items = quote.items || [];
  const actions = quote.actions || [];
  const measurements = quote.measurements || [];

  return (
    <div className="min-h-screen" style={{ background: "var(--bg)" }}>
      <div className={isMobile ? "px-4 pt-4 pb-3" : "px-8 pt-8 pb-4"}>
        <div className={`flex ${isMobile ? "flex-col gap-2" : "items-center justify-between"}`}>
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="btn-secondary h-9" data-testid="jobsheet-back">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <h1 className={isMobile ? "text-base font-semibold" : "ui-page-title"}>Contractor Job Sheet</h1>
            {quote.quote_number && <span className="text-sm font-mono" style={{ color: "var(--text-secondary)" }}>{quote.quote_number}</span>}
          </div>
          <span className="badge-pill badge-blue" data-testid="jobsheet-client-badge">
            {quote.client_type}
          </span>
        </div>
      </div>

      <div className={`max-w-4xl mx-auto ${isMobile ? "px-4 pb-4" : "px-8 pb-8"} space-y-6`}>
        {/* Job Info */}
        <section className="ui-surface p-6" data-testid="jobsheet-info">
          <h2 className="text-base font-semibold tracking-tight uppercase mb-4 flex items-center gap-2">
            <MapPin className="w-4 h-4 text-slate-400" /> Job Information
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-xs text-slate-400 uppercase tracking-wide">Client Type</p>
              <p className="font-medium mt-1">{quote.client_type}</p>
            </div>
            <div>
              <p className="text-xs text-slate-400 uppercase tracking-wide">Address</p>
              <p className="font-medium mt-1">{quote.site_address || "—"}</p>
            </div>
            {quote.tenant_phone && (
              <div>
                <p className="text-xs text-slate-400 uppercase tracking-wide flex items-center gap-1"><Phone className="w-3 h-3" /> Tenant Phone</p>
                <p className="font-medium mt-1">{quote.tenant_phone}</p>
              </div>
            )}
            {quote.booked_team_member_name && (
              <div>
                <p className="text-xs text-slate-400 uppercase tracking-wide">Assigned To</p>
                <p className="font-medium mt-1">{quote.booked_team_member_name}</p>
              </div>
            )}
            {quote.start_date && (
              <div>
                <p className="text-xs text-slate-400 uppercase tracking-wide">Scheduled Date</p>
                <p className="font-medium mt-1">{quote.start_date}</p>
              </div>
            )}
            {quote.booked_start_time && quote.booked_end_time && (
              <div>
                <p className="text-xs text-slate-400 uppercase tracking-wide">Time</p>
                <p className="font-medium mt-1">{quote.booked_start_time} — {quote.booked_end_time}</p>
              </div>
            )}
          </div>
        </section>

        {/* Parking */}
        {quote.parking_type && (
          <section className="ui-surface p-6" data-testid="jobsheet-parking">
            <h2 className="text-base font-semibold tracking-tight uppercase mb-3">Parking</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div><p className="text-xs text-slate-400 uppercase">Type</p><p className="font-medium mt-1">{quote.parking_type}</p></div>
              {quote.parking_hours > 0 && <div><p className="text-xs text-slate-400 uppercase">Hours</p><p className="font-medium mt-1">{quote.parking_hours}</p></div>}
              {quote.parking_rate > 0 && <div><p className="text-xs text-slate-400 uppercase">Rate</p><p className="font-medium mt-1 font-mono">{formatCurrency(quote.parking_rate)}/hr</p></div>}
              {quote.parking_hours > 0 && quote.parking_rate > 0 && <div><p className="text-xs text-slate-400 uppercase">Total</p><p className="font-medium mt-1 font-mono">{formatCurrency(quote.parking_hours * quote.parking_rate)}</p></div>}
            </div>
          </section>
        )}

        {/* Actions */}
        {actions.length > 0 && (
          <section className="bg-white border border-slate-200 rounded-sm" data-testid="jobsheet-actions">
            <div className="px-6 py-4 border-b border-slate-200">
              <h2 className="text-base font-semibold tracking-tight uppercase flex items-center gap-2">
                <ClipboardList className="w-4 h-4 text-slate-400" /> Actions ({actions.length})
              </h2>
            </div>
            <div>
              {(() => {
                let mainNum = 0, subNum = 0;
                return actions.map((action, i) => {
                  const act = typeof action === "string" ? { description: action, hours: 0, is_sub: false } : action;
                  const isSub = act.is_sub || false;
                  if (!isSub) { mainNum++; subNum = 0; } else { subNum++; }
                  const label = isSub ? `${mainNum}.${subNum}` : `${mainNum}`;
                  return (
                    <div key={i} className={`flex items-start gap-3 px-6 py-3 border-b border-slate-100 last:border-0 text-sm ${isSub ? "pl-10" : ""}`}>
                      <span className={`text-xs font-mono w-8 text-right flex-shrink-0 mt-0.5 ${isSub ? "text-slate-300" : "text-slate-500 font-semibold"}`}>{label}</span>
                      <div className="flex-1">
                        {!isSub && act.title && <p className="font-semibold">{act.title}</p>}
                        <p className={isSub ? "text-slate-500" : "text-slate-600"}>{act.description || "—"}</p>
                      </div>
                      {!isSub && (act.hours > 0) && (
                        <span className="text-xs text-slate-500 font-mono mt-0.5 flex-shrink-0">{formatDuration(act.hours)}</span>
                      )}
                    </div>
                  );
                });
              })()}
            </div>
          </section>
        )}

        {/* Materials */}
        {items.length > 0 && (
          <section className="bg-white border border-slate-200 rounded-sm overflow-hidden" data-testid="jobsheet-materials">
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
              <h2 className="text-base font-semibold tracking-tight uppercase flex items-center gap-2">
                <Package className="w-4 h-4 text-slate-400" /> Materials ({items.length})
              </h2>
              <span className={`badge-pill ${quote.materials_status === "Ordered" || quote.materials_status === "Not Applicable" ? "badge-success" : "badge-danger"}`}>
                {quote.materials_status || "Not Started"}
              </span>
            </div>
            <div>
              {items.map((item, i) => {
                const isClickCollect = (item.location || "").toLowerCase().includes("click and collect");
                return (
                  <div key={item.id} className="px-6 py-4 border-b border-slate-100 last:border-0" data-testid={`jobsheet-item-${i}`}>
                    <div className="flex items-start gap-4">
                      {/* Ordered checkbox */}
                      <button
                        onClick={async () => {
                          const newVal = !item.ordered;
                          setQuote(prev => ({ ...prev, items: prev.items.map(it => it.id === item.id ? { ...it, ordered: newVal } : it) }));
                          try {
                            await updateItem(id, item.id, { ordered: newVal });
                            // Auto-update materials_status
                            const updatedItems = quote.items.map(it => it.id === item.id ? { ...it, ordered: newVal } : it);
                            const allOrdered = updatedItems.every(it => it.ordered);
                            if (allOrdered && updatedItems.length > 0) {
                              setQuote(prev => ({ ...prev, materials_status: "Ordered" }));
                              await updateQuote(id, { materials_status: "Ordered" });
                            } else if (!allOrdered && quote.materials_status === "Ordered") {
                              setQuote(prev => ({ ...prev, materials_status: "Not Started" }));
                              await updateQuote(id, { materials_status: "Not Started" });
                            }
                          } catch { toast.error("Failed to update"); }
                        }}
                        className="w-7 h-7 rounded-md border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors"
                        style={{ borderColor: item.ordered ? "var(--success)" : "var(--border)", background: item.ordered ? "var(--success)" : "transparent" }}
                        data-testid={`jobsheet-ordered-${i}`}
                      >
                        {item.ordered && <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>}
                      </button>

                      <div className="flex-1 min-w-0">
                        {/* Item name + supplier */}
                        <p className="text-sm font-semibold" style={{ color: "var(--text-primary)" }}>{item.item_name || "—"}</p>
                        <div className="flex items-center gap-3 mt-1 text-xs" style={{ color: "var(--text-secondary)" }}>
                          <span className="badge-pill badge-blue text-[10px]">{item.supplier}</span>
                          <span>{item.location || "—"}</span>
                          <span>Qty: {item.qty}</span>
                        </div>

                        {/* Branch location for Click & Collect */}
                        {isClickCollect && (
                          <div className="mt-2">
                            <Input
                              value={item.branch_location || ""}
                              onChange={(e) => {
                                const val = e.target.value;
                                setQuote(prev => ({ ...prev, items: prev.items.map(it => it.id === item.id ? { ...it, branch_location: val } : it) }));
                              }}
                              onBlur={(e) => {
                                const val = e.target.value;
                                updateItem(id, item.id, { branch_location: val }).catch(() => toast.error("Failed to save"));
                              }}
                              onKeyDown={(e) => e.key === "Enter" && e.target.blur()}
                              placeholder="Branch location (e.g. Stratford)"
                              className="h-8 text-sm rounded-lg w-full max-w-xs"
                              style={{ border: "1px solid var(--border)" }}
                              data-testid={`jobsheet-branch-${i}`}
                            />
                          </div>
                        )}

                        {/* Order number */}
                        <div className="mt-2">
                          <Input
                            value={item.order_number || ""}
                            onChange={(e) => {
                              const val = e.target.value;
                              setQuote(prev => ({ ...prev, items: prev.items.map(it => it.id === item.id ? { ...it, order_number: val } : it) }));
                            }}
                            onBlur={(e) => {
                              const val = e.target.value;
                              updateItem(id, item.id, { order_number: val }).catch(() => toast.error("Failed to save"));
                              // Auto-fill branch for items with same order number
                              if (val && isClickCollect) {
                                const match = quote.items.find(it => it.id !== item.id && it.order_number === val && it.branch_location);
                                if (match) {
                                  setQuote(prev => ({ ...prev, items: prev.items.map(it => it.id === item.id ? { ...it, branch_location: match.branch_location } : it) }));
                                  updateItem(id, item.id, { branch_location: match.branch_location }).catch(() => {});
                                  toast.success(`Branch auto-filled: ${match.branch_location}`);
                                }
                              }
                            }}
                            onKeyDown={(e) => e.key === "Enter" && e.target.blur()}
                            placeholder="Order #"
                            className="h-8 text-sm rounded-lg font-mono w-full max-w-xs"
                            style={{ border: "1px solid var(--border)" }}
                            data-testid={`jobsheet-order-num-${i}`}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* Material Purchases */}
        <section className="bg-white border border-slate-200 rounded-sm p-6" data-testid="jobsheet-receipts">
          <h2 className="text-base font-semibold tracking-tight uppercase mb-4 flex items-center gap-2">
            <FileText className="w-4 h-4 text-slate-400" /> Material Purchases
          </h2>
          {receipts.length > 0 && (
            <div className="space-y-2 mb-4">
              {receipts.map(r => (
                <div key={r.id} className="flex items-center gap-3 py-2 px-3 bg-slate-50 rounded-sm border border-slate-100">
                  <FileText className="w-4 h-4 text-slate-400 flex-shrink-0" />
                  <a
                    href={getMediaUrl(r.storage_path)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm font-medium text-sky-600 hover:underline flex-1 truncate"
                  >
                    {r.original_filename}
                  </a>
                  <span className="text-xs text-slate-400">{r.size ? `${(r.size / 1024).toFixed(1)} KB` : ""}</span>
                  <a
                    href={getMediaUrl(r.storage_path)}
                    download={r.original_filename}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-slate-400 hover:text-sky-500 transition-colors"
                    title="Download"
                    data-testid={`download-receipt-${r.id}`}
                  >
                    <Download className="w-3.5 h-3.5" />
                  </a>
                  <button
                    onClick={async () => {
                      try {
                        await deleteMedia(id, r.id);
                        toast.success("Deleted");
                        loadQuote();
                      } catch { toast.error("Failed to delete"); }
                    }}
                    className="text-slate-300 hover:text-red-500 transition-colors"
                    data-testid={`delete-receipt-${r.id}`}
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
          <label className={`flex items-center justify-center gap-2 py-3 border-2 border-dashed border-slate-300 rounded-sm cursor-pointer hover:border-slate-400 hover:bg-slate-50 transition-colors ${uploading ? "opacity-50 pointer-events-none" : ""}`}>
            <Upload className="w-4 h-4 text-slate-400" />
            <span className="text-sm text-slate-500">{uploading ? "Uploading..." : "Upload receipt or invoice"}</span>
            <input
              type="file"
              accept="image/*,.pdf,.doc,.docx"
              multiple
              className="hidden"
              onChange={async (e) => {
                const files = Array.from(e.target.files || []).slice(0, 20);
                if (files.length === 0) return;
                setUploading(true);
                toast.success(`${files.length} file${files.length > 1 ? "s" : ""} uploaded`);
                loadQuote();
                setUploading(false);
                e.target.value = "";
              }}
              data-testid="receipt-upload-input"
            />
          </label>
        </section>

        {/* Work Report */}
        {quote.work_report && (
          <section className="ui-surface p-6" data-testid="jobsheet-work-report">
            <h2 className="text-base font-semibold tracking-tight uppercase mb-3 flex items-center gap-2">
              <FileText className="w-4 h-4 text-slate-400" /> Work Report
            </h2>
            <p className="text-sm whitespace-pre-wrap" style={{ color: "var(--text-primary)" }}>{quote.work_report}</p>
          </section>
        )}

        {/* Measurements */}
        {measurements.length > 0 && (
          <section className="bg-white border border-slate-200 rounded-sm p-6" data-testid="jobsheet-measurements">
            <h2 className="text-base font-semibold tracking-tight uppercase mb-4 flex items-center gap-2">
              <Ruler className="w-4 h-4 text-slate-400" /> Measurements
            </h2>
            <div className="space-y-2">
              {measurements.map((m, i) => (
                <div key={i} className="flex items-center gap-4 py-2 border-b border-slate-100 last:border-0 text-sm">
                  <span className="font-medium flex-1">{m.label || "—"}</span>
                  <span className="font-mono">{m.value}</span>
                  <span className="text-slate-400 text-xs uppercase">{m.unit}</span>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Photos */}
        {photos.length > 0 && (
          <section className="bg-white border border-slate-200 rounded-sm p-6" data-testid="jobsheet-photos">
            <h2 className="text-base font-semibold tracking-tight uppercase mb-4 flex items-center gap-2">
              <ImageIcon className="w-4 h-4 text-slate-400" /> Photos ({photos.length})
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {photos.map(p => (
                <div
                  key={p.id}
                  className="aspect-square rounded-sm overflow-hidden bg-slate-100 border border-slate-200 cursor-pointer hover:ring-2 hover:ring-orange-400 transition-all"
                  onClick={() => setEnlargedPhoto(p)}
                >
                  <img src={getMediaUrl(p.storage_path)} alt={p.original_filename} className="w-full h-full object-cover" />
                </div>
              ))}
            </div>
          </section>
        )}
      </div>

      {/* Enlarged photo */}
      <Dialog open={!!enlargedPhoto} onOpenChange={() => setEnlargedPhoto(null)}>
        <DialogContent className="p-2">
          {enlargedPhoto && (
            <div className="w-full">
              <img src={getMediaUrl(enlargedPhoto.storage_path)} alt={enlargedPhoto.original_filename} className="w-full h-auto max-h-[80vh] object-contain rounded-sm" />
              <p className="text-xs text-slate-500 mt-2 text-center">{enlargedPhoto.original_filename}</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
